var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Félix Robles <felix@sequentech.io>
// SPDX-FileCopyrightText: 2023 Kevin Nguyen <kevin@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useContext, useMemo } from "react";
import { Outlet, ScrollRestoration, useLocation, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Footer, Header, PageBanner } from "@sequentech/ui-essentials";
import { EVotingPortalCountdownPolicy } from "@sequentech/ui-core";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./providers/AuthContextProvider";
import { SettingsContext } from "./providers/SettingsContextProvider";
import { ApolloWrapper } from "./providers/ApolloContextProvider";
import { VotingPortalError, VotingPortalErrorType } from "./services/VotingPortalError";
import { useAppSelector } from "./store/hooks";
import { selectElectionIds } from "./store/elections/electionsSlice";
import { selectBallotStyleByElectionId, selectBallotStyleElectionIds, selectFirstBallotStyle, } from "./store/ballotStyles/ballotStylesSlice";
import WatermarkBackground from "./components/WaterMark/Watermark";
import SequentLogo from "@sequentech/ui-essentials/public/Sequent_logo.svg";
import BlankLogoImg from "@sequentech/ui-essentials/public/blank_logo.svg";
var StyledApp = styled(Stack)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    min-height: 100vh;\n    ", "\n"], ["\n    min-height: 100vh;\n    ", "\n"])), function (_a) {
    var customCss = _a.customCss;
    return customCss;
});
var HeaderWithContext = function () {
    var _a, _b, _c, _d;
    var authContext = useContext(AuthContext);
    var globalSettings = useContext(SettingsContext).globalSettings;
    var eventId = useParams().eventId;
    var ballotStyle = useAppSelector(selectFirstBallotStyle);
    var presentation = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation;
    var languagesList = (_b = (_a = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _a === void 0 ? void 0 : _a.enabled_language_codes) !== null && _b !== void 0 ? _b : ["en"];
    var showUserProfile = (_c = presentation === null || presentation === void 0 ? void 0 : presentation.show_user_profile) !== null && _c !== void 0 ? _c : true;
    var countdownPolicy = useMemo(function () {
        var _a;
        return (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) === null || _a === void 0 ? void 0 : _a.voting_portal_countdown_policy;
    }, [ballotStyle]);
    var logoImg = (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === undefined
        ? BlankLogoImg
        : (presentation === null || presentation === void 0 ? void 0 : presentation.logo_url) === null
            ? SequentLogo
            : presentation === null || presentation === void 0 ? void 0 : presentation.logo_url;
    return (_jsx(Header, { appVersion: { main: globalSettings.APP_VERSION }, appHash: { main: globalSettings.APP_HASH }, userProfile: {
            firstName: authContext.firstName,
            username: authContext.username,
            email: authContext.email,
            openLink: showUserProfile ? authContext.openProfileLink : undefined,
        }, languagesList: languagesList, logoutFn: authContext.isAuthenticated ? authContext.logout : undefined, logoUrl: logoImg, expiry: {
            alertAt: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_alert_anticipation_secs,
            countdown: (_d = countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.policy) !== null && _d !== void 0 ? _d : EVotingPortalCountdownPolicy.NO_COUNTDOWN,
            countdownAt: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_anticipation_secs,
            endTime: authContext.getExpiry(),
            duration: countdownPolicy === null || countdownPolicy === void 0 ? void 0 : countdownPolicy.countdown_anticipation_secs,
        } }));
};
var App = function () {
    var _a, _b;
    var navigate = useNavigate();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var location = useLocation();
    var _c = useParams(), tenantId = _c.tenantId, eventId = _c.eventId;
    var _d = useContext(AuthContext), isAuthenticated = _d.isAuthenticated, setTenantEvent = _d.setTenantEvent;
    var electionIds = useAppSelector(selectElectionIds);
    var ballotStyleElectionIds = useAppSelector(selectBallotStyleElectionIds);
    var ballotStyle = useAppSelector(function (state) {
        var _a;
        var electionId = (_a = electionIds[0]) !== null && _a !== void 0 ? _a : ballotStyleElectionIds[0];
        return electionId ? selectBallotStyleByElectionId(String(electionId))(state) : undefined;
    });
    useEffect(function () {
        if (location.pathname === "/") {
            throw new VotingPortalError(VotingPortalErrorType.NO_ELECTION_EVENT);
        }
    }, [
        globalSettings.DEFAULT_TENANT_ID,
        globalSettings.DEFAULT_EVENT_ID,
        globalSettings.DISABLE_AUTH,
        navigate,
        location.pathname,
    ]);
    useEffect(function () {
        var isDemo = sessionStorage.getItem("isDemo");
        if (!isAuthenticated && !globalSettings.DISABLE_AUTH && isDemo) {
            var areaId = sessionStorage.getItem("areaId");
            var documentId = sessionStorage.getItem("documentId");
            var publicationId = sessionStorage.getItem("publicationId");
            navigate("/preview/".concat(tenantId, "/").concat(documentId, "/").concat(areaId, "/").concat(publicationId));
            window.location.reload();
        }
        else if (!isAuthenticated && !!tenantId && !!eventId) {
            setTenantEvent(tenantId, eventId, location.pathname.includes("/enroll") ? "register" : "login");
        }
    }, [tenantId, eventId, isAuthenticated, setTenantEvent, globalSettings.DISABLE_AUTH]);
    return (_jsxs(StyledApp, __assign({ className: "voting-portal app-root", customCss: (_b = (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) === null || _a === void 0 ? void 0 : _a.css) !== null && _b !== void 0 ? _b : "" }, { children: [_jsx(ScrollRestoration, {}), _jsxs(ApolloWrapper, { children: [_jsx(HeaderWithContext, {}), _jsxs(PageBanner, __assign({ marginBottom: "auto", sx: { display: "flex", position: "relative", flex: 1 } }, { children: [_jsx(WatermarkBackground, {}), _jsx(Outlet, {})] }))] }), _jsx(Footer, {})] })));
};
export default App;
var templateObject_1;
